import type { JobPricingSnapshot } from './job';
import type { Timesheet } from './timesheet';
import {
  intervalMinutes,
  registeredIntervals,
  subtractIntervals,
  workAfterUnpaidMinutes,
} from './workIntervals';

/** Global commercial defaults at config/clientTimeBilling; copied into order pricing. */
export interface ClientTimeBillingPolicy {
  deductBreaks: boolean;
  breakMinutes: number;
  thresholdHours: number;
  minimumBillableHours: number;
}

export interface ClientTimeBillingConfig extends ClientTimeBillingPolicy {
  updatedAt: string;
  updatedBy: string;
  /**
   * M-D4b — +1 on every save that changes a value; a copy of each version is
   * kept at `config/clientTimeBilling/versions/{version}` (staff-only).
   * Absent on settings saved before versioning (= version 0).
   */
  version?: number;
  /** When this version took effect — the moment it was saved (no scheduling in v1). */
  effectiveFrom?: string;
  /**
   * M-D4b rollout switch: when true, NEW orders get
   * `pricing.minimumRule.scope = 'place_shift'` (one minimum per ordered
   * worker place and shift). Default false. Existing orders never change.
   */
  placeShiftEnabled?: boolean;
  /**
   * M-D9 switch: when true, RM24's own cost (actual and expected) adds the
   * premiums on a worker's card to the base pay (night, public holiday,
   * overtime; no weekend premium until an agreement can be recorded). Default
   * false: every worker carries seeded multipliers that were never confirmed
   * as pay rules, so switching this on moves margins and margin-floor checks.
   * Off = cost exactly as before. Client money and payroll hours are unaffected either way.
   */
  applyWorkerMultipliersToCost?: boolean;
}

/** `config/clientTimeBilling/versions/{version}` — one saved version. */
export interface ClientTimeBillingVersion extends ClientTimeBillingConfig {
  version: number;
  effectiveFrom: string;
}

export const CLIENT_TIME_BILLING_DEFAULTS: Readonly<ClientTimeBillingPolicy> = {
  deductBreaks: true,
  breakMinutes: 30,
  thresholdHours: 6,
  minimumBillableHours: 8,
};

/** Same validation in the settings form and server; zero minimum is intentional. */
export function validClientTimeBilling(p: ClientTimeBillingPolicy): boolean {
  return (
    typeof p.deductBreaks === 'boolean' &&
    Number.isInteger(p.breakMinutes) &&
    p.breakMinutes >= 1 &&
    p.breakMinutes <= 240 &&
    Number.isFinite(p.thresholdHours) &&
    p.thresholdHours > 0 &&
    p.thresholdHours <= 24 &&
    Number.isFinite(p.minimumBillableHours) &&
    p.minimumBillableHours >= 0 &&
    p.minimumBillableHours <= 24
  );
}

/**
 * Revenue-only adjustment of a submitted sheet; payroll totals are never changed.
 * Legacy orders continue using their net total. New orders use registered
 * intervals directly with the locked commercial rule. Explicit breaks take priority;
 * their duration is not deducted twice. No gaps between entries are invented as work.
 * billableShiftGroups supplies the complete shift to this calculation.
 */
export function clientHoursForTimesheet(
  ts: Pick<Timesheet, 'totalHours' | 'entries' | 'autoLunch'>,
  policy?: ClientTimeBillingPolicy,
): { hours: number; breakMinutes: number } {
  const net = Math.max(0, ts.totalHours ?? 0);
  const automatic = ts.autoLunch && !ts.autoLunch.cancelled ? ts.autoLunch.minutes : 0;
  if (!policy) return { hours: net, breakMinutes: automatic || 0 };
  const intervals = registeredIntervals(ts.entries ?? []);
  const gross = intervalMinutes(intervals.work);
  const explicit = intervalMinutes(intervals.pauses);
  // The threshold uses work excluding recorded breaks, before any automatic
  // deduction. A long period never creates a second monetary deduction.
  const worked = gross - explicit;
  const proposed =
    !ts.autoLunch?.cancelled && explicit === 0 && worked > policy.thresholdHours * 60
      ? policy.breakMinutes
      : 0;
  const unpaid =
    ts.autoLunch?.basis === 'confirmed' && !ts.autoLunch.cancelled
      ? (ts.autoLunch.confirmedMinutes ?? ts.autoLunch.minutes)
      : Math.max(explicit, proposed);
  const deducted = policy.deductBreaks ? Math.min(gross, Math.max(0, unpaid)) : 0;
  return { hours: Math.round(((gross - deducted) / 60) * 100) / 100, breakMinutes: deducted };
}

/** Same commercial hours and minimum in every client-facing billing surface. */
export function billableTimesheet(
  ts: Pick<Timesheet, 'totalHours' | 'entries' | 'autoLunch'>,
  pricing?: JobPricingSnapshot,
) {
  const result =
    pricing?.segment === 'b2c'
      ? { hours: intervalMinutes(registeredIntervals(ts.entries ?? []).work) / 60, breakMinutes: 0 }
      : clientHoursForTimesheet(ts, pricing?.clientTimeBilling);
  const minimum = pricing?.segment === 'b2c' ? 0 : (pricing?.minimumBillableHours ?? 0);
  return { ...result, billedHours: result.hours > 0 ? Math.max(result.hours, minimum) : 0 };
}

/** Stable shift grouping; a legacy sheet remains its own shift until explicitly continued. */
export function timesheetShiftKey(ts: Partial<Timesheet>, fallback = ''): string {
  return JSON.stringify([ts.clientId, ts.jobId, ts.workerId, ts.shiftId ?? ts.id ?? fallback]);
}

/** One commercial pause and minimum per shift. Callers must pass all members. */
export function billableShiftGroups(sheets: Timesheet[], pricing?: JobPricingSnapshot) {
  const groups = new Map<string, Timesheet[]>();
  sheets.forEach((ts, index) => {
    if (ts.status === 'rejected' && ts.finalRejection) return;
    const key = timesheetShiftKey(ts, String(index));
    groups.set(key, [...(groups.get(key) ?? []), ts]);
  });
  return [...groups].map(([key, members]) => {
    const ordered = [...members].sort(
      (a, b) =>
        (a.entries[0]?.checkInAt ?? '').localeCompare(b.entries[0]?.checkInAt ?? '') ||
        (a.id ?? '').localeCompare(b.id ?? ''),
    );
    const cancelled = ordered.find((ts) => ts.autoLunch?.cancelled)?.autoLunch;
    const confirmed = ordered.filter(
      (ts) => ts.autoLunch?.basis === 'confirmed' && !ts.autoLunch.cancelled,
    );
    const merged = {
      entries: ordered.flatMap((ts) => ts.entries),
      totalHours: ordered.reduce((sum, ts) => sum + (ts.totalHours ?? 0), 0),
      autoLunch: confirmed.length
        ? {
            ...confirmed[0].autoLunch!,
            confirmedMinutes: ordered.reduce(
              (sum, ts) =>
                sum +
                (ts.autoLunch?.basis === 'confirmed' && !ts.autoLunch.cancelled
                  ? (ts.autoLunch.confirmedMinutes ?? ts.autoLunch.minutes)
                  : intervalMinutes(registeredIntervals(ts.entries).pauses)),
              0,
            ),
          }
        : (cancelled ??
          (ordered.some((ts) => ts.autoLunch)
            ? {
                ...ordered.find((ts) => ts.autoLunch)!.autoLunch!,
                minutes: ordered.reduce((sum, ts) => sum + (ts.autoLunch?.minutes ?? 0), 0),
              }
            : undefined)),
    };
    const plannedMinutes = ordered.find((ts) => ts.shiftPlan?.breakMinutes != null)?.shiftPlan
      ?.breakMinutes;
    const result = billableTimesheet(
      merged,
      pricing?.clientTimeBilling && plannedMinutes != null
        ? {
            ...pricing,
            clientTimeBilling: { ...pricing.clientTimeBilling, breakMinutes: plannedMinutes },
          }
        : pricing,
    );
    return { key, members: ordered, ...result };
  });
}

/** Allocate a shift total for row displays without charging the minimum on each row. */
export function billableWorkRows(sheets: Timesheet[], pricing?: JobPricingSnapshot) {
  const result = new Map<string, ReturnType<typeof billableTimesheet>>();
  for (const group of billableShiftGroups(sheets, pricing)) {
    const rows = group.members.map((ts) => {
      const intervals = registeredIntervals(ts.entries);
      return {
        ts,
        gross: intervalMinutes(intervals.work),
        explicit: intervalMinutes(intervals.pauses),
        deducted: 0,
      };
    });
    const pause = workAfterUnpaidMinutes(
      group.members.flatMap((ts) => ts.entries),
      group.breakMinutes,
      (pricing?.clientTimeBilling?.thresholdHours ?? 6) * 60,
    );
    let unallocated = [...pause.recorded, ...pause.assumed];
    for (const row of rows) {
      const work = registeredIntervals(row.ts.entries).work;
      const intersection = subtractIntervals(work, subtractIntervals(work, unallocated));
      row.deducted = intervalMinutes(intersection);
      unallocated = subtractIntervals(unallocated, intersection);
    }
    let remainingHours = group.hours;
    let uplift = group.billedHours - group.hours;
    rows.forEach((row, index) => {
      const hours =
        index === rows.length - 1
          ? Math.max(0, remainingHours)
          : Math.min(remainingHours, Math.round(((row.gross - row.deducted) / 60) * 100) / 100);
      remainingHours -= hours;
      const minimum = hours > 0 ? uplift : 0;
      if (hours > 0) uplift = 0;
      result.set(`${row.ts.jobId}/${row.ts.id}`, {
        hours,
        billedHours: hours + minimum,
        breakMinutes: row.deducted,
      });
    });
  }
  return result;
}

/** Statuses whose time the client is (or will be) billed for. */
const CLIENT_HOUR_STATUSES = new Set(['submitted', 'approved', 'invoiced']);

/**
 * PB-05 — the client's billable hours per sheet (`${jobId}/${id}`) for every
 * client-facing view: the order's commercial pause rule, ONE minimum per
 * (order, shift) for B2B, B2C full registered presence without a minimum.
 * Never the payroll `totalHours`. Draft and rejected sheets carry no client
 * hours. Callers must pass every member of each shift they show (a whole
 * order or a window wide enough to hold whole shifts) — a partial shift
 * would place its minimum on the wrong row.
 */
export function clientBilledHoursBySheet(
  sheets: Timesheet[],
  pricingOf: (jobId: string) => JobPricingSnapshot | undefined,
): Map<string, ReturnType<typeof billableTimesheet>> {
  const byJob = new Map<string, Timesheet[]>();
  for (const ts of sheets) {
    if (!CLIENT_HOUR_STATUSES.has(ts.status)) continue;
    const list = byJob.get(ts.jobId) ?? [];
    list.push(ts);
    byJob.set(ts.jobId, list);
  }
  const out = new Map<string, ReturnType<typeof billableTimesheet>>();
  for (const [jobId, list] of byJob)
    for (const [key, value] of billableWorkRows(list, pricingOf(jobId))) out.set(key, value);
  return out;
}
